import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { ToolbarIconButton } from '@plato/ui/candidates/toolbar-icon-button';
import { PolymorphicButtonBase } from '@plato/ui/primitives/button';
import { InputGroupAddon } from '@plato/ui/primitives/input-group';
import { Typography } from '@plato/ui/primitives/typography';
import { cn } from '@plato/ui/util/classes';
import { BarChart3Icon, CalendarCheckIcon, MicIcon, TrendingDownIcon } from 'lucide-react';
import { useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { RecordingDialog } from '../../voice-recording/components/recording-dialog.tsx';
import { PlatoBust } from '../avatar/plato-bust.tsx';
import { useAskPlato } from './ask-plato-provider.tsx';
import { PromptInput, PromptInputSubmit, PromptInputTextarea } from './prompt-input.tsx';

// Touch bumps every control to the 44px target, as in the panel's composer.
const touchTarget = '[@media(hover:none)]:h-11 [@media(hover:none)]:min-w-11';

/**
 * Ask Plato on the home dashboard, laid out like a chat start page: a
 * greeting, one large composer, and starter questions picked from what reps
 * actually ask first. Questions open the chat panel and land in its
 * conversation, so the card holds no chat of its own.
 *
 * The visit-prep starter opens the chat with the question pre-filled and the
 * cursor where the customer name goes: without one the chat only asks back
 * "which customer?". German puts the name mid-sentence ("…Termin bei [Kunde] vor"),
 * so the copy comes in a before and an after part.
 */
export function AskPlatoHomeCard(): ReactElement {
    const { t } = useTranslation('ask-plato');
    const { askPlato, draftAskPlato } = useAskPlato();
    const sendEvent = useAnalytics();
    const { data: me } = api.platoUser.me.getMe.useQuery();
    const [draft, setDraft] = useState('');

    const ask = (question: string): void => {
        if (question.trim().length === 0) {
            return;
        }
        askPlato(question, 'home_card');
        setDraft('');
    };

    const hour = new Date().getHours();
    const greetingOptions = { name: me?.firstName ?? '' };
    const greeting =
        hour < 11
            ? t('home_card__greeting_morning', greetingOptions)
            : hour < 18
              ? t('home_card__greeting_day', greetingOptions)
              : t('home_card__greeting_evening', greetingOptions);

    return (
        <section className="mx-auto flex w-full max-w-3xl flex-col gap-default pt-relaxed pb-relaxed mb-relaxed">
            <div className="flex flex-col items-center gap-tight text-center">
                <Typography variant="display">{greeting}</Typography>
                {/* Plato speaks the first-person line, with the bust from the corner
                    button and the panel, so the card reads as Ask Plato. Phones skip it:
                    the subtitle wraps there, and the top bar shows the bust already. */}
                <div className="flex items-center gap-cozy">
                    <span className="hidden size-6 shrink-0 overflow-hidden rounded-full ring-1 ring-line-weak sm:block">
                        <PlatoBust speaking={false} blink follow />
                    </span>
                    <Typography variant="body" className="text-content-weak">
                        {t('home_card__subtitle')}
                    </Typography>
                </div>
            </div>

            <PromptInput
                className="min-h-14 rounded-full bg-container px-cozy shadow-md"
                onSubmit={(event) => {
                    event.preventDefault();
                    ask(draft);
                }}
            >
                <PromptInputTextarea
                    className="py-default"
                    rows={1}
                    name="prompt"
                    value={draft}
                    onChange={(event) => {
                        setDraft(event.target.value);
                    }}
                    placeholder={t('placeholder')}
                />
                <InputGroupAddon align="inline-end">
                    <ToolbarIconButton
                        label={t('voice__start_recording', { ns: 'common' })}
                        className={touchTarget}
                        onClick={() => {
                            void DialogStateManager.show(RecordingDialog, {
                                trackingContext: { source: 'ask_plato' },
                                onTranscribed: (text) => {
                                    setDraft((current) =>
                                        current.trim().length > 0 ? `${current} ${text}` : text,
                                    );
                                },
                            });
                        }}
                    >
                        <MicIcon />
                    </ToolbarIconButton>
                    <PromptInputSubmit
                        className={cn('rounded-full', touchTarget)}
                        disabled={draft.trim().length === 0}
                        label={t('send')}
                    />
                </InputGroupAddon>
            </PromptInput>

            {/* Pills centred under the composer; stacked full width on a phone. */}
            <ul className="flex flex-col gap-cozy sm:flex-row sm:flex-wrap sm:justify-center">
                <StarterQuestion
                    icon={<CalendarCheckIcon />}
                    onClick={() => {
                        sendEvent('ai_chat_starter_question_selected', { question: 'visit_prep' });
                        const [before = '', after = ''] = t('home_card__starter_visit_prep').split(
                            '{{caret}}',
                        );
                        draftAskPlato({ before, after }, 'home_card');
                    }}
                >
                    {t('home_card__starter_visit_prep_label')}
                </StarterQuestion>
                <StarterQuestion
                    icon={<TrendingDownIcon />}
                    onClick={() => {
                        sendEvent('ai_chat_starter_question_selected', { question: 'revenue_decline' });
                        ask(t('home_card__starter_revenue_decline'));
                    }}
                >
                    {t('home_card__starter_revenue_decline_label')}
                </StarterQuestion>
                <StarterQuestion
                    icon={<BarChart3Icon />}
                    onClick={() => {
                        sendEvent('ai_chat_starter_question_selected', { question: 'revenue_vs_last_year' });
                        ask(t('home_card__starter_revenue_vs_last_year'));
                    }}
                >
                    {t('home_card__starter_revenue_vs_last_year_label')}
                </StarterQuestion>
            </ul>
        </section>
    );
}

type StarterQuestionProps = {
    icon: ReactNode;
    onClick: () => void;
    children: ReactNode;
};

function StarterQuestion({ icon, onClick, children }: StarterQuestionProps): ReactElement {
    return (
        <li>
            <PolymorphicButtonBase
                onClick={onClick}
                className={cn(
                    'flex min-h-11 w-full cursor-pointer items-center gap-cozy rounded-full border border-line-weak',
                    'bg-container px-default py-tight text-left transition-colors sm:w-auto',
                    'hover:border-line hover:bg-container-hover',
                    '[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-content-weak',
                )}
            >
                {icon}
                <Typography variant="body">{children}</Typography>
            </PolymorphicButtonBase>
        </li>
    );
}
